import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dimensions, Platform, ScrollView, StyleSheet, Text, Pressable, View, Modal, TextInput } from 'react-native';
import StepIndicator from './StepIndicator';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import DataOps from '../../../../data/DataOps';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { accountTypes, AccountType } from '../../../../interface/AccountType';
import ChildrenManagementContent from '../../../../components/child/ChildrenManagementContent';
import { Child } from '../../../../interface/Child';
import { createDefaultChild } from '../../../../utils/childUtils';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG, GOOGLE_PLACES_CONFIG } from '../../../../constants/GoogleMapsConfig';
import AddressAutocomplete from '../../../../components/general/AddressAutocomplete';
import { useDropzone } from 'react-dropzone';
import CampTypes from '../../../../data/CampTypes';
import PhoneNumberInput from '../../../../components/general/PhoneNumberInput';
import { formatPhoneDisplay } from '../../../../sms/utils/phoneFormatting';
import { validatePhoneNumber } from '../../../../sms/utils/phoneValidation';
import CampGeoHelper from '../../../../utility/CampGeoHelper';
import { identifyUser, sanitizeTelemetryError, trackEvent } from '../../../../utility/telemetry';

const SCREEN_WIDTH = Dimensions.get('window').width;
const IS_DESKTOP = SCREEN_WIDTH >= 900;

interface SignUpWizardProps {
  visible: boolean;
  onClose: () => void;
  phoneNumber: string;
  firebaseUid: string;
  entrySource?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
}

interface SetupAccountData {
  accountType: AccountType;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  children: Child[];
  businessName: string;
  businessLicense: string;
  website: string;
  selectedCategories: string[];
  campContactEmail: string;
  campPhone: string;
  campStreet: string;
  campCity: string;
  campState: string;
  campZipCode: string;
  campCountry: string;
  campDescription: string;
  specialization: string;
  yearsExperience: string;
  certifications: string;
}

const STEP_ACCOUNT_TYPE = 'accountType';
const STEP_BASIC_INFO = 'basicInfo';
const STEP_FAMILY = 'family';
const STEP_HOST = 'host';
const STEP_HOST_INFO = 'hostInfo';
const STEP_HOST_DOCUMENTS = 'hostDocuments';
const STEP_COACH = 'coach';
const STEP_COMPLETE = 'complete';


const Field = ({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = 'default',
  multiline = false,
  editable = true,
}: {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  keyboardType?: 'default' | 'email-address' | 'numeric' | 'phone-pad' | 'url';
  multiline?: boolean;
  editable?: boolean;
}) => (
  <View style={styles.inputGroup}>
    <Text style={styles.label}>{label}</Text>
    <TextInput
      style={[styles.input, multiline && styles.textArea, !editable && styles.readOnlyInput]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#999"
      keyboardType={keyboardType}
      autoCapitalize={keyboardType === 'email-address' ? 'none' : 'sentences'}
      multiline={multiline}
      numberOfLines={multiline ? 4 : 1}
      editable={editable}
    />
  </View>
);


type CircleImageUploadProps = {
  title: string;
  previewUri: string;
  placeholderIcon: string;
  rootProps: any;
  inputProps: any;
};

const CircleImageUpload: React.FC<CircleImageUploadProps> = ({
  title,
  previewUri,
  placeholderIcon,
  rootProps,
  inputProps,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <View style={styles.avatarUploadSection}>
      <Text style={styles.avatarUploadTitle}>{title}</Text>
      <div
        {...rootProps}
        style={{ ...(styles.avatarUploader as any), cursor: 'pointer' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input {...inputProps} />
        {previewUri ? (
          <img src={previewUri} alt={`${title} preview`} style={styles.avatarPreviewImage as any} />
        ) : (
          <View style={styles.avatarPlaceholder}>
            <MaterialCommunityIcons name={placeholderIcon as any} size={44} color="#94A3B8" />
          </View>
        )}
        {isHovered && (
          <View style={styles.avatarHoverOverlay}>
            <MaterialCommunityIcons name="camera-plus" size={26} color="#FFFFFF" />
            <Text style={styles.avatarHoverText}>Upload</Text>
          </View>
        )}
      </div>
    </View>
  );
};

const SignUpWizard: React.FC<SignUpWizardProps> = ({
  visible,
  onClose,
  phoneNumber,
  firebaseUid,
  entrySource = 'unknown',
  email = '',
  firstName = '',
  lastName = '',
  userName = '',
}) => {
  const { setUser, setLikedCamps } = useContext(GlobalStateContext);
  const [isSaving, setIsSaving] = useState(false);
  const isGoogleUser = Boolean(email);
  const autocompleteRef = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [campSearchQuery, setCampSearchQuery] = useState('');
  const [isHostAddressSameAsCamp, setIsHostAddressSameAsCamp] = useState(false);
  const { isLoaded } = useLoadScript(GOOGLE_MAPS_CONFIG);
  const placesService = useRef<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [campLogoFile, setCampLogoFile] = useState<File | null>(null);
  const [hostProfileImageFile, setHostProfileImageFile] = useState<File | null>(null);
  const [campLogoPreview, setCampLogoPreview] = useState('');
  const [hostProfilePreview, setHostProfilePreview] = useState('');

  const onDrop = (acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file: file // Store the actual file object for later upload if needed
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.jpeg', '.jpg', '.png']
    }
  });

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fileToDataUrl = (file: File) => (
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    })
  );

  const onCampLogoDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setCampLogoFile(file);
    try {
      setCampLogoPreview(await fileToDataUrl(file));
    } catch (error) {
      console.error('Failed to preview camp logo file', error);
      setCampLogoPreview('');
    }
  };

  const onHostProfileDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setHostProfileImageFile(file);
    try {
      setHostProfilePreview(await fileToDataUrl(file));
    } catch (error) {
      console.error('Failed to preview host profile file', error);
      setHostProfilePreview('');
    }
  };

  const { getRootProps: getCampLogoRootProps, getInputProps: getCampLogoInputProps } = useDropzone({
    onDrop: onCampLogoDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxFiles: 1,
    multiple: false,
  });

  const { getRootProps: getHostProfileRootProps, getInputProps: getHostProfileInputProps } = useDropzone({
    onDrop: onHostProfileDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxFiles: 1,
    multiple: false,
  });

  const uploadSelectedImage = async (file: File | null, imageIdPrefix: string) => {
    if (!file) return '';
    const objectUrl = URL.createObjectURL(file);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const imageId = `${imageIdPrefix}_${Date.now()}_${safeName}`;
      const uploadedUrl = await DataOps.uploadImage(objectUrl, imageId);
      return uploadedUrl || '';
    } catch (error) {
      console.error('Error uploading selected image:', error);
      return '';
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  useEffect(() => {
    if (isLoaded && (window as any).google && !placesService.current) {
      const dummy = document.createElement('div');
      placesService.current = new (window as any).google.maps.places.PlacesService(dummy);
    }
  }, [isLoaded]);

  const handleAutocompleteSelect = (placeId: string, description: string) => {
    if (!placesService.current) {
      setSearchQuery(description);
      return;
    }

    placesService.current.getDetails(
      {
        placeId: placeId,
        fields: ['address_components', 'formatted_address', 'geometry']
      },
      (place: any, status: any) => {
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
          if (!place?.address_components) return;

          let streetNumber = '';
          let route = '';
          let city = '';
          let stateValue = '';
          let zipCode = '';
          let countryValue = '';

          place.address_components.forEach((component: any) => {
            const types = component.types || [];
            if (types.includes('street_number')) streetNumber = component.long_name;
            if (types.includes('route')) route = component.long_name;
            if (types.includes('locality')) city = component.long_name;
            if (types.includes('administrative_area_level_1')) stateValue = component.short_name;
            if (types.includes('postal_code')) zipCode = component.long_name;
            if (types.includes('country')) countryValue = component.long_name;
          });

          setAccountData((prev) => ({
            ...prev,
            street: `${streetNumber} ${route}`.trim(),
            city,
            state: stateValue,
            zipCode,
            country: countryValue || prev.country,
          }));
          setSearchQuery(place.formatted_address || '');
        } else {
          setSearchQuery(description);
        }
      }
    );
  };

  const handleCampAutocompleteSelect = (placeId: string, description: string) => {
    if (!placesService.current) {
      setCampSearchQuery(description);
      return;
    }

    placesService.current.getDetails(
      {
        placeId: placeId,
        fields: ['address_components', 'formatted_address', 'geometry']
      },
      (place: any, status: any) => {
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
          if (!place?.address_components) return;

          let streetNumber = '';
          let route = '';
          let city = '';
          let stateValue = '';
          let zipCode = '';
          let countryValue = '';

          place.address_components.forEach((component: any) => {
            const types = component.types || [];
            if (types.includes('street_number')) streetNumber = component.long_name;
            if (types.includes('route')) route = component.long_name;
            if (types.includes('locality')) city = component.long_name;
            if (types.includes('administrative_area_level_1')) stateValue = component.short_name;
            if (types.includes('postal_code')) zipCode = component.long_name;
            if (types.includes('country')) countryValue = component.long_name;
          });

          setAccountData((prev) => ({
            ...prev,
            campStreet: `${streetNumber} ${route}`.trim(),
            campCity: city,
            campState: stateValue,
            campZipCode: zipCode,
            campCountry: countryValue || prev.campCountry,
          }));
          setCampSearchQuery(place.formatted_address || '');
        } else {
          setCampSearchQuery(description);
        }
      }
    );
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [accountData, setAccountData] = useState<SetupAccountData>({
    accountType: 'family',
    firstName,
    lastName,
    email,
    phone: isGoogleUser ? '' : phoneNumber,
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    children: [],
    businessName: '',
    businessLicense: '',
    website: '',
    selectedCategories: [],
    campContactEmail: email || '',
    campPhone: isGoogleUser ? '' : phoneNumber,
    campStreet: '',
    campCity: '',
    campState: '',
    campZipCode: '',
    campCountry: 'USA',
    campDescription: '',
    specialization: '',
    yearsExperience: '',
    certifications: '',
  });

  const stepIds = useMemo(() => {
    const steps = [STEP_ACCOUNT_TYPE];
    if (accountData.accountType === 'host') {
      // Requested host flow order: camp info (old step4) -> camp category (old step3) -> host info (old step2)
      steps.push(STEP_HOST_INFO, STEP_HOST, STEP_BASIC_INFO, STEP_HOST_DOCUMENTS);
    } else {
      steps.push(STEP_BASIC_INFO);
      if (accountData.accountType === 'family') steps.push(STEP_FAMILY);
      else if (accountData.accountType === 'coach') steps.push(STEP_COACH);
    }
    steps.push(STEP_COMPLETE);
    return steps;
  }, [accountData.accountType]);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === stepIds.length - 1;

  useEffect(() => {
    if (!visible) {
      return;
    }

    trackEvent('signup_wizard_viewed', {
      entry_source: entrySource,
      account_type: accountData.accountType,
    });
  }, [visible, entrySource]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    trackEvent('signup_wizard_step_viewed', {
      entry_source: entrySource,
      account_type: accountData.accountType,
      step_id: stepIds[currentStep],
      step_index: currentStep + 1,
      total_steps: stepIds.length,
    });
  }, [visible, entrySource, accountData.accountType, currentStep, stepIds]);

  // Google Maps Autocomplete logic is now handled by AddressAutocomplete component

  const updateField = <K extends keyof SetupAccountData>(field: K, value: SetupAccountData[K]) => {
    setAccountData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'accountType') {
      trackEvent('signup_account_type_selected', {
        entry_source: entrySource,
        account_type: value,
      });
    }
  };

  const handleWizardClose = () => {
    trackEvent('signup_wizard_closed', {
      entry_source: entrySource,
      account_type: accountData.accountType,
      step_id: stepIds[currentStep],
    });
    onClose();
  };

  const copyCampAddressToHostAddress = () => {
    if (!accountData.campStreet && !accountData.campCity && !accountData.campState && !accountData.campZipCode) {
      alert('Please add the camp address first in the Camp Information step.');
      return false;
    }

    const composed = [
      accountData.campStreet,
      accountData.campCity,
      accountData.campState,
      accountData.campZipCode,
      accountData.campCountry,
    ].filter(Boolean).join(', ');

    setAccountData((prev) => ({
      ...prev,
      street: prev.campStreet || prev.street,
      city: prev.campCity || prev.city,
      state: prev.campState || prev.state,
      zipCode: prev.campZipCode || prev.zipCode,
      country: prev.campCountry || prev.country,
    }));
    setSearchQuery(composed);
    return true;
  };

  const toggleHostAddressSameAsCamp = () => {
    if (isHostAddressSameAsCamp) {
      setIsHostAddressSameAsCamp(false);
      return;
    }
    const copied = copyCampAddressToHostAddress();
    if (copied) {
      setIsHostAddressSameAsCamp(true);
    }
  };

  const validateCurrentStep = () => {
    const stepId = stepIds[currentStep];

    if (stepId === STEP_ACCOUNT_TYPE) {
      if (!accountData.accountType) {
        alert('Please select an account type');
        return false;
      }
      return true;
    }

    if (stepId === STEP_BASIC_INFO) {
      if (!accountData.firstName.trim() || accountData.firstName.trim().length < 2) {
        alert('Please enter a valid first name.');
        return false;
      }

      if (!accountData.lastName.trim() || accountData.lastName.trim().length < 2) {
        alert('Please enter a valid last name.');
        return false;
      }

      if (!accountData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountData.email.trim())) {
        alert('Please enter a valid email address.');
        return false;
      }

      if (accountData.phone.trim()) {
        const phoneValidation = validatePhoneNumber(accountData.phone.trim());
        if (!phoneValidation.ok) {
          alert(phoneValidation.msg);
          return false;
        }
      }

      return true;
    }

    if (stepId === STEP_HOST) {
      if (!accountData.selectedCategories || accountData.selectedCategories.length === 0) {
        alert('Please choose at least one camp category.');
        return false;
      }

      return true;
    }

    if (stepId === STEP_HOST_INFO) {
      if (!accountData.businessName.trim()) {
        alert('Please enter your camp name.');
        return false;
      }

      if (!accountData.campContactEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountData.campContactEmail.trim())) {
        alert('Please enter a valid camp contact email.');
        return false;
      }

      if (!accountData.campPhone.trim()) {
        alert('Please enter a camp phone number.');
        return false;
      }
      const campPhoneValidation = validatePhoneNumber(accountData.campPhone.trim());
      if (!campPhoneValidation.ok) {
        alert(campPhoneValidation.msg);
        return false;
      }

      if (!accountData.campStreet.trim() || !accountData.campCity.trim() || !accountData.campState.trim() || !accountData.campZipCode.trim()) {
        alert('Please complete the camp address (street, city, state, zip code).');
        return false;
      }
      return true;
    }

    if (stepId === STEP_HOST_DOCUMENTS) {
      // Documents are optional at this stage - user can skip and add later
      return true;
    }

    if (stepId === STEP_COACH) {
      if (!accountData.specialization.trim()) {
        alert('Please enter your specialization');
        return false;
      }

      return true;
    }

    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, stepIds.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleFinish = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    setIsSaving(true);

    try {
      let hostProfileImageUrl = '';
      let campLogoUrl = '';
      if (accountData.accountType === 'host') {
        hostProfileImageUrl = await uploadSelectedImage(hostProfileImageFile, `host_profile_${firebaseUid}`);
        campLogoUrl = await uploadSelectedImage(campLogoFile, `camp_logo_${firebaseUid}`);
      }

      const baseUser = {
        userId: firebaseUid,
        authId: firebaseUid,
        email: accountData.email.trim(),
        firstName: accountData.firstName.trim(),
        lastName: accountData.lastName.trim(),
        userName: userName || `${accountData.firstName.trim()} ${accountData.lastName.trim()}`.trim(),
        phone: accountData.phone.trim(),
        address: {
          street: accountData.street.trim(),
          city: accountData.city.trim(),
          state: accountData.state.trim(),
          zipCode: accountData.zipCode.trim(),
          country: accountData.country.trim() || 'USA',
        },
        // Store canonical 'user' value matching mobile schema.
        // UI uses 'family' / 'host' / 'coach' but Firestore always gets 'user' / 'host' / 'coach'.
        accountType: accountData.accountType === 'family' ? 'user' : accountData.accountType,
        emailVerified: isGoogleUser,
        phoneVerified: !isGoogleUser,
        primaryContactMethod: isGoogleUser ? 'email' : 'phone',
        dateJoined: new Date().toISOString(),
        likedCamps: [] as string[],
        likedSessions: [] as string[],
        paymentMethods: [] as any[],
      };

      let userData: any;

      if (accountData.accountType === 'family') {
        const validChildren = accountData.children.filter(
          (child) => child.firstName.trim() || child.lastName.trim(),
        );

        // Stamp parentId and timestamps, keep all other fields the child modal collected
        const childDocs = validChildren.map((child) => ({
          ...child,
          accountType: 'child',
          userId: child.userId,
          childId: child.userId,
          parentId: firebaseUid,
          updatedAt: new Date().toISOString(),
          createdAt: (child as any).createdAt || new Date().toISOString(),
          deleted: false,
        }));

        // Derive user-level emergency contact from the first child's EC (or parent's own info)
        const firstEC = childDocs[0]?.emergencyContact;
        const emergencyContact = {
          name: firstEC
            ? `${firstEC.firstName} ${firstEC.lastName}`.trim()
            : `${accountData.firstName.trim()} ${accountData.lastName.trim()}`.trim(),
          phone: firstEC?.phone || accountData.phone,
          relationship: firstEC?.relationship || 'Parent',
        };

        userData = {
          ...baseUser,
          accountType: 'user',
          children: childDocs,
          activeChildId: undefined,
          camps: [],
          emergencyContact,
        };
      } else if (accountData.accountType === 'host') {
        userData = {
          ...baseUser,
          accountType: 'host',
          profileImage: hostProfileImageUrl || '',
          camps: [],
          coaches: [],
          businessName: accountData.businessName.trim(),
          businessLicense: accountData.businessLicense.trim(),
          campWebsite: accountData.website.trim(),
          likedCamps: [],
          likedSessions: [],
        };
      } else {
        userData = {
          ...baseUser,
          accountType: 'coach',
          hostIds: [],
          assignedCamps: [],
          assignedSessions: [],
          specialties: accountData.specialization.trim() ? [accountData.specialization.trim()] : [],
          yearsOfExperience: accountData.yearsExperience.trim() ? Number(accountData.yearsExperience.trim()) || 0 : 0,
          certifications: accountData.certifications
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          likedCamps: [],
          likedSessions: [],
        };
      }

      await DataOps.saveUserByAccountType(userData.userId, userData, accountData.accountType, false);

      // Mirror each child as a separate document in the 'child' collection (matches mobile schema).
      if (accountData.accountType === 'family' && userData.children?.length > 0) {
        await Promise.all(
          userData.children.map((child: any) =>
            DataOps.saveCustomDataToFirebase('child', child.childId, child, false),
          ),
        );
      }

      if (accountData.accountType === 'host') {
        const campId = `camp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const campCategory = accountData.selectedCategories?.[0] || 'General';
        const campAddress = [
          accountData.campStreet,
          accountData.campCity,
          accountData.campState,
          accountData.campZipCode,
          accountData.campCountry,
        ].filter(Boolean).join(', ');
        const isLocalhostCamp = typeof window !== 'undefined'
          && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
        const campEnvironment = isLocalhostCamp ? 'test' : 'prod';

        let campData: any = {
          campId,
          hostId: userData.userId,
          campName: accountData.businessName.trim(),
          campDescription: accountData.campDescription.trim(),
          campCategory,
          campAddress,
          street: accountData.campStreet.trim(),
          city: accountData.campCity.trim(),
          state: accountData.campState.trim(),
          zipcode: accountData.campZipCode.trim(),
          country: (accountData.campCountry || 'USA').trim(),
          address: {
            street: accountData.campStreet.trim(),
            city: accountData.campCity.trim(),
            state: accountData.campState.trim(),
            zipCode: accountData.campZipCode.trim(),
            country: (accountData.campCountry || 'USA').trim(),
          },
          contactEmail: accountData.campContactEmail.trim(),
          campPhone: accountData.campPhone.trim(),
          campWebsite: accountData.website.trim(),
          campFileUrls: uploadedFiles.map((file) => ({ name: file.name, url: file.name })),
          campImageUrls: [],
          campLogoUrl: campLogoUrl || '',
          fullyVerified: uploadedFiles.length > 0,
          environment: campEnvironment,
          isTestCamp: campEnvironment === 'test',
          isProdCamp: campEnvironment === 'prod',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isActive: true,
        };

        // Align with host wizard camp schema by adding geolocation/geohash when address is available.
        campData = await CampGeoHelper.enrichCampWithGeolocation(campData);

        await DataOps.saveCustomDataToFirebase('camp', campId, campData, true);
        userData.camps = [campId];
        await DataOps.saveCustomDataToFirebase('host', userData.userId, { camps: [campId] }, true);
      }

      const collectionName = DataOps.getCollectionNameForAccountType(
        accountData.accountType === 'family' ? 'user' : accountData.accountType,
      );
      const dbUser = await DataOps.fetchDataFromFirebase(collectionName, userData.userId);
      await DataOps.saveDataToStorage('user', dbUser);

      setLikedCamps(new Set(dbUser.likedCamps || []));
      setUser(dbUser);
      identifyUser(dbUser.userId, {
        account_type: userData.accountType,
        auth_provider: entrySource,
      });
      trackEvent('signup_wizard_completed', {
        entry_source: entrySource,
        account_type: userData.accountType,
      });

      onClose();
    } catch (error) {
      console.error('Error creating user account:', error);
      trackEvent('signup_wizard_failed', {
        entry_source: entrySource,
        account_type: accountData.accountType,
        ...sanitizeTelemetryError(error, 'signup_wizard_failed'),
      });
      alert('Failed to create account. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderStep = () => {
    const currentStepId = stepIds[currentStep];

    if (currentStepId === STEP_ACCOUNT_TYPE) {
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent}>
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>Welcome to SnapCamp</Text>
            <Text style={styles.stepSubtitle}>Choose the type of account you want to create.</Text>
          </View>

          <View style={styles.typeContainer}>
            {accountTypes.map((account) => {
              const isSelected = accountData.accountType === account.type;
              return (
                <Pressable
                  key={account.type}
                  style={[
                    styles.typeCard,
                    isSelected && {
                      borderColor: account.color,
                      borderWidth: 2,
                      backgroundColor: `${account.color}10`,
                    },
                  ]}
                  onPress={() => updateField('accountType', account.type)}
                >
                  <View style={[styles.iconContainer, { backgroundColor: account.color }]}>
                    <MaterialCommunityIcons name={account.icon as any} size={30} color="#fff" />
                  </View>
                  <Text style={styles.typeTitle}>{account.title}</Text>
                  <Text style={styles.typeDescription}>{account.description}</Text>
                  {isSelected && (
                    <View style={styles.selectedBadge}>
                      <MaterialCommunityIcons name={"check-circle" as any} size={22} color={account.color} />
                    </View>
                  )}
                </Pressable>
              );
            })}
          </View>
        </ScrollView>
      );
    }

    if (currentStepId === STEP_BASIC_INFO) {
      const isHost = accountData.accountType === 'host';
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} keyboardShouldPersistTaps="handled">
          {Platform.OS === 'web' && (
            <style>
              {`
                .pac-container {
                  z-index: 99999 !important;
                  position: fixed !important;
                  background: #fff !important;
                  border-radius: 12px !important;
                  border: 1px solid #E5E7EB !important;
                  box-shadow: 0 12px 28px rgba(0,0,0,0.14) !important;
                }
                .pac-item {
                  padding: 10px 12px !important;
                  font-size: 14px !important;
                }
                .dropzone-active {
                  border-color: #3C8248 !important;
                  background-color: #F0FDF4 !important;
                }
              `}
            </style>
          )}

          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>{isHost ? 'Host/Business Owner Information' : 'Basic information'}</Text>
            <Text style={styles.stepSubtitle}>
              {isHost
                ? 'Tell us about the business owner so we can set up your host profile.'
                : 'Tell us a little about yourself before continuing.'}
            </Text>
          </View>

          {isHost && Platform.OS === 'web' && (
            <CircleImageUpload
              title="Host Profile Image"
              previewUri={hostProfilePreview}
              placeholderIcon="account-circle-outline"
              rootProps={getHostProfileRootProps()}
              inputProps={getHostProfileInputProps()}
            />
          )}

          <View style={styles.row}>
            <View style={[styles.halfWidth]}>
              <Field
                label="First Name *"
                value={accountData.firstName}
                onChangeText={(text) => updateField('firstName', text)}
                placeholder="Enter first name"
              />
            </View>
            <View style={[styles.halfWidth]}>
              <Field
                label="Last Name *"
                value={accountData.lastName}
                onChangeText={(text) => updateField('lastName', text)}
                placeholder="Enter last name"
              />
            </View>
          </View>

          <Field
            label="Email *"
            value={accountData.email}
            onChangeText={(text) => updateField('email', text)}
            placeholder="your.email@example.com"
            keyboardType="email-address"
            editable={!isGoogleUser}
          />
          <PhoneNumberInput
            label="Phone Number"
            value={accountData.phone}
            onChangeText={(digits) => updateField('phone', digits)}
            placeholder={isGoogleUser ? "Enter your phone number" : "Verified phone number"}
            editable={isGoogleUser}
            allowEmpty
          />

          <View style={styles.sectionHeader}>
            <View style={styles.sectionHeaderInline}>
              <Text style={styles.sectionTitle}>{isHost ? 'Host Address' : 'Address'}</Text>
              {isHost && (
                <View style={styles.checkboxGroup}>
                  <Pressable style={styles.checkboxToggle} onPress={toggleHostAddressSameAsCamp}>
                    <MaterialCommunityIcons
                      name={(isHostAddressSameAsCamp ? "checkbox-marked" : "checkbox-blank-outline") as any}
                      size={18}
                      color={isHostAddressSameAsCamp ? "#15803D" : "#6B7280"}
                    />
                  </Pressable>
                  <Pressable style={styles.checkboxLabelPressable} onPress={toggleHostAddressSameAsCamp}>
                    <Text style={styles.checkboxToggleText}>Same as camp address</Text>
                  </Pressable>
                </View>
              )}
            </View>
            <Text style={styles.sectionSubtitle}>
              {isHost
                ? 'Optional. If provided, we will send business-related documents to this address; otherwise, we will default to your camp address.'
                : 'Optional. We use this to personalize nearby camp suggestions.'}
            </Text>
          </View>
          {Platform.OS === 'web' && !isHostAddressSameAsCamp ? (
            <AddressAutocomplete
              value={searchQuery}
              onChangeText={setSearchQuery}
              onSelectPlace={handleAutocompleteSelect}
              placeholder="Start typing your address..."
              containerStyle={{ marginBottom: 16 }}
            />
          ) : (
            <Field label="Street Address" value={accountData.street} onChangeText={(text) => updateField('street', text)} placeholder="123 Main St" />
          )}

          <Field
            label="Street Address"
            value={accountData.street}
            onChangeText={(text) => updateField('street', text)}
            placeholder="123 Main St"
            editable={!isHostAddressSameAsCamp}
          />
          <View style={styles.row}>
            <View style={[styles.halfWidth]}>
              <Field label="City" value={accountData.city} onChangeText={(text) => updateField('city', text)} placeholder="City" editable={!isHostAddressSameAsCamp} />
            </View>
            <View style={[styles.halfWidth]}>
              <Field label="State" value={accountData.state} onChangeText={(text) => updateField('state', text)} placeholder="State" editable={!isHostAddressSameAsCamp} />
            </View>
          </View>
          <View style={styles.row}>
            <View style={[styles.halfWidth]}>
              <Field label="Zip Code" value={accountData.zipCode} onChangeText={(text) => updateField('zipCode', text)} placeholder="12345" keyboardType="numeric" editable={!isHostAddressSameAsCamp} />
            </View>
            <View style={[styles.halfWidth]}>
              <Field label="Country" value={accountData.country} onChangeText={(text) => updateField('country', text)} placeholder="Country" editable={!isHostAddressSameAsCamp} />
            </View>
          </View>
        </ScrollView>
      );
    }

    if (currentStepId === STEP_HOST) {
      const categoryList = CampTypes.filter((type) => type.text.toLowerCase() !== 'all');
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} keyboardShouldPersistTaps="handled">
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>What camp types do you host?</Text>
            <Text style={styles.stepSubtitle}>Choose at least one category that best fits your camp.</Text>
          </View>

          <View style={styles.chipGrid}>
            {categoryList.map((item) => {
              const isActive = accountData.selectedCategories.includes(item.text);
              return (
                <Pressable
                  key={item.text}
                  style={[styles.chip, isActive && styles.chipActive]}
                  onPress={() => updateField('selectedCategories', [item.text])}
                >
                  <MaterialIcons name={item.iconName as any} size={18} color={isActive ? '#14532D' : '#6B7280'} />
                  <Text style={[styles.chipLabel, isActive && styles.chipLabelActive]}>{item.text}</Text>
                </Pressable>
              );
            })}
          </View>
        </ScrollView>
      );
    }

    if (currentStepId === STEP_HOST_INFO) {
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} keyboardShouldPersistTaps="handled">
          {Platform.OS === 'web' && (
            <style>
              {`
                .pac-container {
                  z-index: 99999 !important;
                  position: fixed !important;
                  background: #fff !important;
                  border-radius: 12px !important;
                  border: 1px solid #E5E7EB !important;
                  box-shadow: 0 12px 28px rgba(0,0,0,0.14) !important;
                }
              `}
            </style>
          )}
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>Tell us about your camp</Text>
            <Text style={styles.stepSubtitle}>Families will see this information on your listing.</Text>
          </View>

          {Platform.OS === 'web' && (
            <CircleImageUpload
              title="Camp Logo"
              previewUri={campLogoPreview}
              placeholderIcon="image-outline"
              rootProps={getCampLogoRootProps()}
              inputProps={getCampLogoInputProps()}
            />
          )}

          <Field
            label="Camp Name *"
            value={accountData.businessName}
            onChangeText={(text) => updateField('businessName', text)}
            placeholder="e.g. Sunrise Soccer Academy"
          />
          <Field
            label="Camp Website"
            value={accountData.website}
            onChangeText={(text) => updateField('website', text)}
            placeholder="https://www.yourcamp.com"
            keyboardType="url"
          />
          <Field
            label="Contact Email *"
            value={accountData.campContactEmail}
            onChangeText={(text) => updateField('campContactEmail', text)}
            placeholder="hello@yourcamp.com"
            keyboardType="email-address"
          />
          <PhoneNumberInput
            label="Contact Phone"
            value={accountData.campPhone}
            onChangeText={(digits) => updateField('campPhone', digits)}
            placeholder="(555) 123-4567"
            required
            allowEmpty={false}
          />

          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Camp Location</Text>
          </View>
          {Platform.OS === 'web' ? (
            <AddressAutocomplete
              value={campSearchQuery}
              onChangeText={setCampSearchQuery}
              onSelectPlace={handleCampAutocompleteSelect}
              placeholder="Search for camp address..."
              containerStyle={{ marginBottom: 16 }}
            />
          ) : (
            <Field label="Street Address *" value={accountData.campStreet} onChangeText={(text) => updateField('campStreet', text)} placeholder="123 Main St" />
          )}

          <Field label="Street Address *" value={accountData.campStreet} onChangeText={(text) => updateField('campStreet', text)} placeholder="123 Main St" />
          <View style={styles.row}>
            <View style={[styles.halfWidth]}>
              <Field label="City *" value={accountData.campCity} onChangeText={(text) => updateField('campCity', text)} placeholder="City" />
            </View>
            <View style={[styles.halfWidth]}>
              <Field label="State *" value={accountData.campState} onChangeText={(text) => updateField('campState', text)} placeholder="State" />
            </View>
          </View>
          <View style={styles.row}>
            <View style={[styles.halfWidth]}>
              <Field label="Zip Code *" value={accountData.campZipCode} onChangeText={(text) => updateField('campZipCode', text)} placeholder="12345" keyboardType="numeric" />
            </View>
            <View style={[styles.halfWidth]}>
              <Field label="Country" value={accountData.campCountry} onChangeText={(text) => updateField('campCountry', text)} placeholder="Country" />
            </View>
          </View>

          <Field
            label="Camp Description"
            value={accountData.campDescription}
            onChangeText={(text) => updateField('campDescription', text)}
            placeholder="Share what makes your camp special."
            multiline
          />

        </ScrollView>
      );
    }

    if (currentStepId === STEP_HOST_DOCUMENTS) {
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} keyboardShouldPersistTaps="handled">
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>Business Documents</Text>
            <Text style={styles.stepSubtitle}>
              These documents are optional for now, but will be required when you publish your first course. You can skip this step and upload them later.
            </Text>
          </View>

          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Licenses & Permits (Optional)</Text>
            <Text style={styles.sectionSubtitle}>We periodically validate all camps regarding their business licenses to ensure every listed camp is permitted to operate under local and federal laws. Uploading your documents now helps us verify your camp more quickly.</Text>
          </View>

          {Platform.OS === 'web' ? (
            <View>
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${isDragActive ? '#3C8248' : '#D1D5DB'}`,
                  borderRadius: '12px',
                  padding: '22px 20px',
                  textAlign: 'center',
                  backgroundColor: isDragActive ? '#F0FDF4' : '#F9FAFB',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <input {...getInputProps()} />
                <View style={{ alignItems: 'center' }}>
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 20,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isDragActive ? '#DCFCE7' : '#EEF2FF',
                      marginBottom: 6,
                    }}
                  >
                    <MaterialCommunityIcons
                      name="file-upload-outline"
                      size={20}
                      color={isDragActive ? '#15803D' : '#4F46E5'}
                    />
                  </View>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#111827' }}>
                    {isDragActive ? 'Drop files to upload' : 'Upload license files'}
                  </Text>
                  <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                    Click to browse or drag files here
                  </Text>
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                    <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 }}>
                      <Text style={{ fontSize: 11, color: '#6B7280' }}>PDF, JPG, PNG</Text>
                    </View>
                    <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 }}>
                      <Text style={{ fontSize: 11, color: '#6B7280' }}>Max 10MB</Text>
                    </View>
                  </View>
                </View>
              </div>

              {uploadedFiles.length > 0 && (
                <View style={{ marginTop: 16 }}>
                  <Text style={{ fontSize: 12, fontWeight: '700', color: '#6B7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Uploaded files
                  </Text>
                  {uploadedFiles.map((file, index) => (
                    <View
                      key={index}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 12,
                        backgroundColor: '#FFFFFF',
                        borderWidth: 1,
                        borderColor: '#E5E7EB',
                        borderRadius: 8,
                        marginBottom: 8,
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                        <MaterialCommunityIcons name="file-document-outline" size={20} color="#6B7280" />
                        <Text style={{ marginLeft: 8, fontSize: 14, color: '#111827' }} numberOfLines={1}>
                          {file.name}
                        </Text>
                      </View>
                      <Pressable onPress={() => removeFile(index)}>
                        <MaterialCommunityIcons name="close" size={18} color="#9CA3AF" />
                      </Pressable>
                    </View>
                  ))}
                </View>
              )}
            </View>
          ) : (
            <Field
              label="Business License Number"
              value={accountData.businessLicense}
              onChangeText={(text) => updateField('businessLicense', text)}
              placeholder="Enter license number (optional)"
            />
          )}
        </ScrollView>
      );
    }

    if (currentStepId === STEP_FAMILY) {
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} keyboardShouldPersistTaps="handled">
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>Add Family Members</Text>
            <Text style={styles.stepSubtitle}>Add your children to your account. You can skip this and do it later.</Text>
          </View>

          <ChildrenManagementContent
            children={accountData.children}
            onChildrenChange={(children) => updateField('children', children)}
            parentId={firebaseUid}
            parentFirstName={accountData.firstName}
            parentLastName={accountData.lastName}
            parentPhone={accountData.phone}
          />
        </ScrollView>
      );
    }

    if (currentStepId === STEP_COACH) {
      return (
        <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} keyboardShouldPersistTaps="handled">
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>Coach setup</Text>
            <Text style={styles.stepSubtitle}>Tell families and hosts what you specialize in.</Text>
          </View>
          <Field
            label="Specialization *"
            value={accountData.specialization}
            onChangeText={(text) => updateField('specialization', text)}
            placeholder="Basketball, soccer, swimming, etc."
          />
          <Field
            label="Years of Experience"
            value={accountData.yearsExperience}
            onChangeText={(text) => updateField('yearsExperience', text)}
            placeholder="Number of years"
            keyboardType="numeric"
          />
          <Field
            label="Certifications"
            value={accountData.certifications}
            onChangeText={(text) => updateField('certifications', text)}
            placeholder="Comma-separated certifications"
            multiline
          />
        </ScrollView>
      );
    }

    const selectedAccount = accountTypes.find((type) => type.type === accountData.accountType);
    const toDisplayValue = (value: string | number | undefined | null) => {
      if (typeof value === 'number') {
        return String(value);
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : 'Not provided';
      }
      return 'Not provided';
    };
    const formatReviewPhone = (phone: string | undefined | null) => {
      const digits = String(phone || '').replace(/\D/g, '').slice(0, 10);
      return digits ? formatPhoneDisplay(digits) : 'Not provided';
    };
    const SummaryRow = ({ icon, label, value }: { icon: string; label: string; value: string | number }) => (
      <View style={styles.summaryRow}>
        <View style={styles.summaryLineLeft}>
          <View style={styles.summaryIconBadge}>
            <MaterialCommunityIcons name={icon as any} size={14} color="#3C8248" />
          </View>
          <Text style={styles.summaryLabel}>{label}</Text>
        </View>
        <Text style={styles.summaryValue}>{toDisplayValue(value)}</Text>
      </View>
    );

    return (
      <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent}>
        <View style={styles.stepHeader}>
          <Text style={styles.stepTitle}>Review and create your account</Text>
          <Text style={styles.stepSubtitle}>Check the details below before finishing setup.</Text>
        </View>

        <View style={styles.summaryCard}>
          <SummaryRow icon="account-circle-outline" label="Account type" value={selectedAccount?.title || accountData.accountType || 'Not provided'} />

          {accountData.accountType === 'host' && (
            <>
              <SummaryRow icon="storefront-outline" label="Organization" value={accountData.businessName || 'Not provided'} />
              <SummaryRow icon="web" label="Website" value={accountData.website || 'Not provided'} />
              <SummaryRow icon="shape-outline" label="Category" value={accountData.selectedCategories?.length ? accountData.selectedCategories.join(', ') : 'Not selected'} />
            </>
          )}

          <SummaryRow icon="account-outline" label="Name" value={`${accountData.firstName} ${accountData.lastName}`.trim() || 'Not provided'} />
          <SummaryRow icon="email-outline" label="Email" value={accountData.email || 'Not provided'} />
          <SummaryRow icon="phone-outline" label="Phone" value={formatReviewPhone(accountData.phone)} />

          {accountData.accountType === 'host' && (
            <>
              <SummaryRow
                icon="map-marker-outline"
                label="Camp Location"
                value={accountData.campStreet && accountData.campCity && accountData.campState
                  ? `${accountData.campStreet}, ${accountData.campCity}, ${accountData.campState} ${accountData.campZipCode || ''}`.trim()
                  : 'Not provided'}
              />
              <SummaryRow icon="email-fast-outline" label="Camp contact email" value={accountData.campContactEmail || 'Not provided'} />
              <SummaryRow icon="phone-in-talk-outline" label="Camp contact phone" value={formatReviewPhone(accountData.campPhone)} />
              <SummaryRow icon="image-outline" label="Camp logo" value={campLogoFile ? campLogoFile.name : 'Not uploaded'} />
              <SummaryRow icon="account-box-outline" label="Host profile image" value={hostProfileImageFile ? hostProfileImageFile.name : 'Not uploaded'} />
              <SummaryRow icon="file-document-multiple-outline" label="Documents uploaded" value={uploadedFiles.length > 0 ? `${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}` : 'None (optional)'} />
            </>
          )}

          {accountData.accountType === 'family' && (
            <SummaryRow icon="account-child-outline" label="Children added" value={accountData.children.filter((child) => child.firstName || child.lastName || child.dateOfBirth).length} />
          )}

          {accountData.accountType === 'coach' && (
            <>
              <SummaryRow icon="medal-outline" label="Specialization" value={accountData.specialization || 'Not provided'} />
              <SummaryRow icon="calendar-clock-outline" label="Years of experience" value={accountData.yearsExperience || 'Not provided'} />
              <SummaryRow icon="certificate-outline" label="Certifications" value={accountData.certifications || 'Not provided'} />
            </>
          )}
        </View>
      </ScrollView>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleWizardClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalCard}>
          <View style={styles.header}>
            <Pressable style={styles.closeButton} onPress={handleWizardClose}>
              <MaterialCommunityIcons name={"close" as any} size={24} color="#666" />
            </Pressable>
            <Text style={styles.headerTitle}>Create Your Account</Text>
            <View style={styles.placeholder} />
          </View>

          <View style={styles.wizardContainer}>
            {renderStep()}
          </View>

          <View style={styles.footer}>
            <StepIndicator currentStep={currentStep} totalSteps={stepIds.length} />

            <View style={styles.navigationContainer}>
              <View style={styles.buttonRow}>
                {!isFirstStep && (
                  <Pressable
                    style={[styles.button, styles.secondaryButton]}
                    onPress={handlePrevious}
                    disabled={isSaving}
                  >
                    <MaterialCommunityIcons name={"chevron-left" as any} size={20} color="#3C8248" />
                    <Text style={styles.secondaryButtonText}>Back</Text>
                  </Pressable>
                )}

                {!isLastStep ? (
                  <Pressable
                    style={[styles.button, styles.primaryButton, isFirstStep && styles.fullWidthButton]}
                    onPress={handleNext}
                  >
                    <Text style={styles.primaryButtonText}>Next</Text>
                    <MaterialCommunityIcons name={"chevron-right" as any} size={20} color="#fff" />
                  </Pressable>
                ) : (
                  <Pressable
                    style={[styles.button, styles.primaryButton, styles.finishButton]}
                    onPress={handleFinish}
                    disabled={isSaving}
                  >
                    <Text style={styles.primaryButtonText}>
                      {isSaving ? 'Creating Account...' : 'Complete'}
                    </Text>
                    <MaterialCommunityIcons name={"check" as any} size={20} color="#fff" />
                  </Pressable>
                )}
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 28,
  },
  modalCard: {
    width: '100%',
    maxWidth: IS_DESKTOP ? 780 : 680,
    height: '100%',
    maxHeight: IS_DESKTOP ? '92%' : '96%',
    backgroundColor: '#F3F7F4',
    borderRadius: 28,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.55)',
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.16,
    shadowRadius: 32,
    elevation: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 10,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  closeButton: {
    padding: 6,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
  },
  placeholder: {
    width: 40,
  },
  wizardContainer: {
    flex: 1,
    minHeight: 0,
  },
  stepScroll: {
    flex: 1,
  },
  stepContent: {
    paddingHorizontal: IS_DESKTOP ? 28 : 22,
    paddingTop: IS_DESKTOP ? 24 : 20,
    paddingBottom: IS_DESKTOP ? 28 : 32,
  },
  stepHeader: {
    marginBottom: 18,
  },
  stepTitle: {
    fontSize: IS_DESKTOP ? 24 : 22,
    fontWeight: '800',
    color: '#0F172A',
    marginBottom: 6,
  },
  stepSubtitle: {
    fontSize: 15,
    lineHeight: 22,
    color: '#64748B',
  },
  typeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    justifyContent: 'center',
  },
  typeCard: {
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderRadius: 20,
    paddingHorizontal: 18,
    paddingVertical: 20,
    width: IS_DESKTOP ? 220 : '100%',
    minHeight: IS_DESKTOP ? 180 : 170,
    borderWidth: 1.5,
    borderColor: '#DDE5DD',
    alignItems: 'center',
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.06,
    shadowRadius: 14,
    elevation: 2,
    position: 'relative',
  },
  iconContainer: {
    width: 62,
    height: 62,
    borderRadius: 31,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  typeTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#0F172A',
    marginBottom: 6,
    textAlign: 'center',
  },
  typeDescription: {
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 19,
  },
  selectedBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#111827',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  sectionSubtitle: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 4,
    lineHeight: 18,
  },
  avatarUploadSection: {
    alignItems: 'center',
    marginBottom: 18,
  },
  avatarUploadTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 10,
  },
  avatarUploader: {
    width: 116,
    height: 116,
    borderRadius: 58,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    overflow: 'hidden',
    backgroundColor: '#F8FAFC',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarPreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarHoverOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.42)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarHoverText: {
    color: '#FFFFFF',
    marginTop: 6,
    fontSize: 12,
    fontWeight: '700',
  },
  readOnlyInput: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
  sectionHeader: {
    marginTop: 8,
    marginBottom: 8,
  },
  sectionHeaderInline: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    gap: 8,
  },
  checkboxGroup: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 4,
    backgroundColor: '#F8FAFC',
    borderColor: '#D1D5DB',
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 2,
    paddingLeft: 4,
    paddingRight: 10,
    marginBottom: 1,
  },
  checkboxToggle: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxLabelPressable: {
    justifyContent: 'center',
    paddingVertical: 2,
    paddingRight: 2,
  },
  checkboxToggleText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#1F2937',
  },
  smallCopyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  smallCopyButtonText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#14532D',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  chipGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  chip: {
    width: '48%',
    marginBottom: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  chipActive: {
    backgroundColor: '#F0FDF4',
    borderColor: '#3C8248',
  },
  chipLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  chipLabelActive: {
    color: '#14532D',
  },
  emptyState: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 14,
    padding: 16,
  },
  emptyStateText: {
    color: '#475569',
    fontSize: 14,
    lineHeight: 22,
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 18,
    padding: 20,
    gap: 14,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  summaryLineLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  summaryIconBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ECFDF5',
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  summaryValue: {
    flex: 1,
    fontSize: 15,
    lineHeight: 22,
    color: '#111827',
    textAlign: 'right',
  },
  footer: {
    backgroundColor: 'rgba(255,255,255,0.97)',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: -3 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 4,
  },
  navigationContainer: {
    paddingHorizontal: 18,
    paddingTop: 8,
    paddingBottom: 10,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 13,
    borderRadius: 12,
    gap: 8,
  },
  primaryButton: {
    backgroundColor: '#3C8248',
  },
  secondaryButton: {
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: '#3C8248',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButtonText: {
    color: '#3C8248',
    fontSize: 16,
    fontWeight: '600',
  },
  fullWidthButton: {
    flex: 1,
  },
  finishButton: {
    backgroundColor: '#3C8248',
  },
});

export default SignUpWizard;

